import {
    Component,
    OnInit,
    OnDestroy,
    ElementRef,
    ViewChild,
    AfterViewInit,
    HostListener,
    Renderer2,
    ViewContainerRef,
    Inject,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule, DOCUMENT } from '@angular/common';
import { LoggerService } from '@wdpr/ra-angular-logger';
import { BehaviorSubject, fromEvent, Subject, asapScheduler } from 'rxjs';
import {
    takeUntil,
    debounceTime,
    map,
    switchMap,
    filter,
    first,
    finalize,
    take
} from 'rxjs/operators';
import head from 'lodash-es/head';
import isEmpty from 'lodash-es/isEmpty';
import keyBy from 'lodash-es/keyBy';
import remove from 'lodash-es/remove';
import get from 'lodash-es/get';
import cloneDeep from 'lodash-es/cloneDeep';

import { ANALYTICS_TIMEOUT } from '@finder/shared/constants/app.constants';
import { ConfigService } from '@finder/core/config.service';
import { DebugPanelService } from '@finder/shared/services/debug-panel/debug-panel.service';
import { DebugService } from '@finder/features/debug/services/debug-service/debug.service';
import { DiningListReservationsService } from './services/dining-list-reservations/dining-list-reservations.service';
import { Facet } from '@finder/shared/services/filter-emitter-service/facet.class';
import { FilterEmitterService } from '@finder/shared/services/filter-emitter-service/filter-emitter.service';
import { FiltersHashService } from '@finder/features/list/services/filters-hash/filters-hash.service';
import { FinderAnalyticsService } from '@finder/shared/services/finder-analytics/finder-analytics.service';
import { IPicture } from '@finder/shared/components/picture/picture.interface';
import {
    ILocationMultiSelectOption,
    IMultiSelectOption
} from '@finder/features/list/components/multi-select/multi-select-option.interface';
import { ListCardOptions } from '@finder/features/list/components/list-card/list-card.interface';
import { ListFiltersComponent } from './components/filters/list-filters.component';
import { ListService } from '@finder/features/list/services/list/list.service';
import {
    MapDataLocationDefaults,
    MapInterface,
    MapDataLocationMarker,
    MapButtonOptions,
    MarkerOptions
} from '@finder/shared/components/map/map/map.interface';
import { MAP_POPUP } from '@finder/shared/components/map/defaults/map.defaults.constants';
import { MultiSelectService } from '@finder/shared/services/multi-select/multi-select.service';
import { MultiSelectServiceFactory, IMultiSelectService } from '@finder/shared/services/multi-select/multi-select-service.interface';
import { RemoveFinderClassesProvider, REMOVE_FINDER_CLASSES } from '@finder/shared/utils/dom/removeFinderClasses';
import { UrlParamsService } from '@finder/shared/services/url-params/url-params.service';
import { ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { FinderThemeService } from '@finder/shared/services/theme-service/finder-theme.service';
import { IListGroup } from './services/list/group.interface';
import { WebSocketsService } from '@finder/shared/services/websockets/websockets.service';
import { TitleComponent } from '@finder/shared/components/title/title.component';
import { MultiSelectComponent } from './components/multi-select/multi-select.component';
import { AnchorComponent } from '@finder/shared/components/anchor/anchor.component';
import { ButtonComponent } from '@finder/shared/components/button/button.component';
import { RaSingleSelectComponent } from '@finder/shared/components/ra-wrappers/ra-single-select/ra-single-select.component';
import { PromoBannerComponent } from '@finder/shared/components/promo-banner/promo-banner.component';
import { PictureComponent } from '@finder/shared/components/picture/picture.component';
import { NoResultsComponent } from './components/no-results/no-results.component';
import { LoadingSpinnerComponent } from '@finder/shared/components/loading-spinner/loading-spinner.component';
import { MapComponent } from '@finder/shared/components/map/map.component';
import { CampaignCtaComponent } from '@finder/shared/components/campaign-cta/campaign-cta.component';
import { ListCardComponent } from './components/list-card/list-card.component';
import { IntersectionDirective } from '@finder/shared/directives/intersection/intersection.directive';

const EXPAND_MAP = 'expand-map';
const MAP_ONLY = 'map-only';
const HIDE_SORT = 'hide-sort';
const HIDE_LIST = 'hide-list';
const HIDE_HR = 'hide-hr';
// Set this to a high number if you want the list to load everything
const MAX_CARDS = 20;

@Component({
    selector: 'finder-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        TitleComponent,
        MultiSelectComponent,
        AnchorComponent,
        ListFiltersComponent,
        ButtonComponent,
        RaSingleSelectComponent,
        PromoBannerComponent,
        PictureComponent,
        NoResultsComponent,
        LoadingSpinnerComponent,
        MapComponent,
        CampaignCtaComponent,
        ListCardComponent,
        IntersectionDirective, // finderIntersection
    ]
})
export class ListComponent implements OnInit, OnDestroy, AfterViewInit {

    @ViewChild('listHeader', { read: ElementRef }) listHeader: ElementRef;
    @ViewChild(ListFiltersComponent) filters: ListFiltersComponent;
    @ViewChild('filters', { read: ElementRef }) filtersElem: ElementRef;

    sortElm: ElementRef;
    @ViewChild('sort', { read: ElementRef }) set sortRef(sortRef: ElementRef) {
        if (sortRef) {
            this.sortElm = sortRef;
        }
    };

    listElm: ElementRef;
    @ViewChild('list', { read: ElementRef }) set listRef(listRef: ElementRef) {
        if (listRef) {
            this.listElm = listRef;
        }
    }

    mapElm: ElementRef;
    @ViewChild('map', { read: ElementRef }) set mapRef(mapRef: ElementRef) {
        if (mapRef) {
            this.mapElm = mapRef;
        }
    }

    hrElm: ElementRef;
    @ViewChild('hr', { read: ElementRef }) set hrRef(hrRef: ElementRef) {
        if (hrRef) {
            this.hrElm = hrRef;
        }
    }

    listData: any;
    appliedFacets: Facet[] = [];
    locationsMap: Record<string, any>;
    mapDefaults: MapDataLocationDefaults;
    listData$: BehaviorSubject<any>;
    rootMarginListCards = '0px 0px 500px 0px';
    thresholdListCards: 1.0;
    listCards: any = {};
    listCardsOptions: ListCardOptions;
    size = 0;
    loaded = false;
    groups: Array<IListGroup>;
    selectedSortOption = 'name';
    sortOptions: Array<any>;
    locationsOptions: ILocationMultiSelectOption[];
    allLocationsOption: IMultiSelectOption;
    locationsSelected = [];
    multiSelect: IMultiSelectService;
    markers: Array<MapDataLocationMarker> = [];
    map: MapInterface;
    destroy$ = new Subject<void>();
    markerOptions: MarkerOptions = {
        showPopup: true,
        keyboard: true,
        allowClustering: true,
        allowMarkerMouseOver: true,
        popupOptions: {
            maxWidth: MAP_POPUP.maxWidth,
            minWidth: MAP_POPUP.maxHeight,
            autoPanPadding: MAP_POPUP.autoPanPadding
        }
    };
    shrinkFilters = false;
    slimFilters = false;
    isDesktop: boolean;
    hashEntityId;
    mapPinFromURI: any;
    debug = false;
    pageBanner: IPicture;
    disableLocationOptions: boolean;
    validatingProcess = false;
    disableMultiSelect: boolean;
    disableMap: boolean;
    disableFilters: boolean;
    hideList = '';
    mapOnly: boolean;
    isRemyEnabled: false;

    protected filterHeight = 0;

    private parkParam: string;
    private expandControl: MapButtonOptions;
    private listControl: MapButtonOptions;
    private expanded = false;

    constructor(
        private route: ActivatedRoute,
        private debugService: DebugService,
        private filterEmitterService: FilterEmitterService,
        private filtersHash: FiltersHashService,
        private finderAnalyticsService: FinderAnalyticsService,
        private listService: ListService,
        private diningListReservations: DiningListReservationsService,
        private multiSelectService: MultiSelectService,
        private viewportService: ViewportDetectionService,
        private logger: LoggerService,
        private renderer: Renderer2,
        private elementRef: ElementRef,
        private winRef: WindowRef,
        private debugPanelService: DebugPanelService,
        private viewContainerRef: ViewContainerRef,
        private urlParamsService: UrlParamsService,
        private configService: ConfigService,
        private finderThemeService: FinderThemeService,
        private webSocketService: WebSocketsService,
        @Inject(DOCUMENT) private document: Document,
        @Inject(REMOVE_FINDER_CLASSES) private removeFinderClasses: RemoveFinderClassesProvider,
    ) { }

    ngOnInit() {
        fromEvent(this.winRef.nativeWindow, 'load').pipe(
            take(1)
        ).subscribe(() => {
            this.sendNativeBridgeEvent();
        });

        this.debug = this.debugService.isDebugEnabled();
        this.isDesktop = this.viewportService.isDesktop();
        this.parkParam = get(this.route, 'snapshot.params.park', '');
        this.listData = get(this.route.snapshot.data, 'list');
        this.listCardsOptions = {
            showEntityType: get(this.listData, 'showEntityType', false),
            showOpenHours: get(this.listData, 'showOpenHours', false)
        };

        // Settings to handle news listings
        this.disableMultiSelect = get(this.listData, 'disableMultiSelect', false);
        this.disableMap = get(this.listData, 'disableMap', false);
        this.disableFilters = get(this.listData, 'disableFilters', false);
        this.size = get(this.listData, 'size', 0);
        this.loaded = get(this.listData, 'loaded', false);
        // Settings to handle news listings

        // Control the new remy dine component
        this.isRemyEnabled = this.listData?.isRemyEnabled;

        this.mapOnly = get(this.listData, 'mapOnly', false);

        this.route.paramMap
            .pipe(takeUntil(this.destroy$))
            .subscribe(params => {
                this.urlParamsService.setParams(params);
            });

        this.getBannerImage();

        // Check for errors before going forward
        if (!this.listData) {
            this.logger.error('List SVC -- No service response');

            return;
        }

        this.finderAnalyticsService.initAnalyticsLibrary(this.listData.analytics);
        this.sortOptions = this.listData.sortOptions;
        this.selectedSortOption = this.listData.defaultSortOption || this.selectedSortOption;
        this.finderAnalyticsService.getGuestModel()
            .pipe(takeUntil(this.destroy$))
            .subscribe(guestModel => {
                // Update the analytics model
                this.finderAnalyticsService.updateAnalyticsModel(
                    null,
                    guestModel,
                    '',
                    this.listData.title,
                    `${this.configService.getValue('siteId')}_${this.urlParamsService.entityType}_sort_${this.selectedSortOption}`);

                this.finderAnalyticsService.updateAnalyticsLoader();
            });

        const selectedSortOptionObject = this.sortOptions.find(so => so.key === this.selectedSortOption);
        const sortOrdering = get(selectedSortOptionObject, 'groupOrder', []);

        // Check the toggles page to show the full list
        const perPageNumber = this.listData?.showFullList ? 10_000 : MAX_CARDS;

        this.listData$ = this.listService.updateListData(this.listData, this.selectedSortOption, sortOrdering, perPageNumber);

        // Pass the labels into the list service
        this.listService.labels = this.listData.labels;
        this.listService.labels.countLabel =
            this.listService.labels.countLabels[this.urlParamsService.entityType] ?? this.listService.labels.countLabels['default'];

        if (this.listData.diningReservations) {
            this.diningListReservations.init(this.listData.diningReservations);
        }

        // locations dropdown
        this.locationsOptions = this.createLocationOptions(this.listData.locations);

        // the "All Disneyworld locations" option is coming from the service response with key '-1'
        // so here we remove the "All Disneyworld locations" option from the list
        // 'remove' method will return an array with only one element
        // and with 'head' we get the first element of the array
        this.allLocationsOption = head(remove(this.locationsOptions, opt => opt.key === '-1'));
        // All Locations option is checked by default.
        this.allLocationsOption.checked = true;

        // Initialize MultiSelect Service
        this.multiSelect = MultiSelectServiceFactory.create({
            options: this.locationsOptions,
            allOption: this.allLocationsOption
        });

        this.multiSelectService.updateOptions(this.multiSelect);
        this.multiSelectService.watchOptions()
            .pipe(
                debounceTime(50),
                takeUntil(this.destroy$)
            )
            .subscribe(multiSelect => {
                this.onMultiSelected(multiSelect);
            });

        this.locationsMap = keyBy(this.listData.locations, 'id');

        // This is where we listen for facet changes
        this.filterEmitterService.watchFilterList()
            .pipe(
                debounceTime(50),
                takeUntil(this.destroy$)
            )
            .subscribe((facetList) => this.onWatchFacetList(facetList));

        this.checkHash();
        this.setupMapDefaults();

        // Remove existing finder classes
        const body = this.document.body;
        this.removeFinderClasses(body, this.renderer);

        // Add CSS class to body
        this.finderThemeService.addClassToBody('finder-list');
        this.finderThemeService.addClassToBody(`finder-list-${this.route.snapshot.params['type']}`);

        // Start the websockets connection
        this.startWebSocketConnection(this.listData?.updatePermissions);
    }

    /* istanbul ignore next */
    getElementRef(): Element {
        /**
         * Created an internal reference to the injected element to make testing easier
         */
        return this.elementRef.nativeElement;
    }

    // Remy flow for iOS needs to load in a hybrid webview. This event is triggered when a Nemo list
    // page is finished loading so the mobile app knows to remove the spinner/overlay
    sendNativeBridgeEvent() {
        const event = new Event('disneyRAWebViewBridgeReady');
        this.winRef.nativeWindow.dispatchEvent(event);
    }

    ngAfterViewInit() {
        // An error occured so we shouldn't continue
        if (!this.listData) {
            return;
        }

        this.viewportService.onViewportResize()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.isDesktop = this.viewportService.isDesktop();
                if (this.shrinkFilters && this.isDesktop) {
                    this.shrinkFiltersAndToggleMap(false);
                }
            });

        this.debugPanelService.createDebugPanel(this.viewContainerRef, this.listData);

        // to fix horizontal scrolling in firefox
        // as per https://myjira.disney.com/browse/GIT-50052
        this.document.body.classList.add('no-horizontal-scroll');
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.diningListReservations.disconnect();

        // Clean up the websocket connection
        this.webSocketService.cleanUp();

        // to fix horizontal scrolling in firefox
        // as per https://myjira.disney.com/browse/GIT-50052
        this.document.body.classList.remove('no-horizontal-scroll');
    }

    checkHash() {
        const hashElements = this.filtersHash.checkHash(
            get(this.listData, 'filters.flatFacets', []),
            this.locationsOptions,
            this.sortOptions
        );

        this.selectedSortOption = hashElements.sortOption ? hashElements.sortOption.key : this.selectedSortOption;
        this.multiSelectService.updateSelectedOptions(this.checkLocationParam(hashElements.locations));

        if (hashElements.entityId) {
            this.hashEntityId = hashElements.entityId;
        }

        if (!hashElements.locations.length && !hashElements.facets.length) {
            this.updateListData();

            return;
        }

        hashElements.facets.forEach(facet => {
            this.filterEmitterService.emitFilter(new Facet(facet.urlFriendlyId, facet.value, true, facet.group));
        });
    }

    onSort(event) {
        const { detail: { key } } = event;
        this.selectedSortOption = key;

        this.finderAnalyticsService.updateModelAndTrack({
            linkId: `${this.configService.getValue('siteId')}_${this.urlParamsService.entityType}_sort_${key}`
        });
        this.updateListData();
    }

    trackByCard(index, item) {
        return item ? item.id : index;
    }

    createLocationOptions(locations: any[]): ILocationMultiSelectOption[] {
        return locations.map(location => ({
            key: `${location.id}`,
            value: location.title,
            urlFriendlyId: location.urlFriendlyId,
            checked: false
        }));
    }

    onMultiSelected(multiSelect: IMultiSelectService) {
        // We should always select the last selected option, and pass it to track as linkId
        // For that, we can get the urlfriendlyid from the options array of objects
        const selectedLocation = multiSelect.options.find(element => element.key === multiSelect.selected.slice(-1)[0]);

        if (selectedLocation && this.locationsSelected.length <= multiSelect.selected.length) {
            // This means that a new location was checked, so we need to track that with analytics
            this.finderAnalyticsService.updateModelAndTrack({
                linkId: `${this.configService.getValue('siteId')}_
                    ${this.urlParamsService.entityType}_location_${selectedLocation['urlFriendlyId']}`
            });
        }

        this.locationsSelected = multiSelect.selected;
        this.updateListData();
    }

    onWatchFacetList(facetList: Facet[]) {
        this.appliedFacets = facetList;
        this.updateListData();
    }

    /**
     * When the map has rendered it will emit a interface so we can
     * interact with it. This will capture that interface
     */
    mapIsReady(mapObj: MapInterface) {
        // Get a reference to the map when it's ready.
        // We'll need it for a lot of things
        this.map = mapObj;
        // Wait till the map is ready to setup the markers and cards
        this.setupMapMarkersAndCards();
        this.setupMapEventListeners();
        this.setupListHeaderSwipe();

        // some entities (e.g., resorts) are map-only, no list. For these, we need to expand the map
        // initially and remove the ability to minimize the map
        if (this.mapOnly) {
            this.expandMap(this.map, null);
            this.mapElm.nativeElement?.classList.add(MAP_ONLY);
        }
    }

    pinsAreDropped(mapObj: MapInterface) {
        this.focusOnEntityMarker();
    }

    focusOnEntityMarker() {
        if (this.hashEntityId && this.mapPinFromURI) {
            this.expandMap(this.map, null);
            this.jumpToMarker(this.mapPinFromURI, true);

            return;
        }

        this.map.focusOnPins();
    }

    jumpToMarker(marker: any, openPopup: boolean = false) {
        this.map.jumpToMarker(marker, openPopup);
    }

    shrinkMapAndExpandFilters() {
        if (!this.isDesktop && this.shrinkFilters) {
            this.shrinkFiltersAndToggleMap(false);
        }
    }

    slideControl() {
        if (this.isDesktop) {
            return;
        }

        // Add expand to the map
        this.mapElm.nativeElement?.classList.add(EXPAND_MAP);
    }

    expandMap(mapObj: MapInterface, $event = null) {
        if ($event) {
            $event.preventDefault();
        }

        // Expand the map
        this.mapElm.nativeElement?.classList.add(EXPAND_MAP);

        // Hide the list and sorting elements
        if (!this.mapOnly) {
            this.sortElm?.nativeElement?.classList.add(HIDE_SORT);
        }
        this.listElm?.nativeElement?.classList.add(HIDE_LIST);
        // Fallback for race condition where list renders after map on deep link
        this.hideList = HIDE_LIST;
        this.hrElm?.nativeElement?.classList.add(HIDE_HR);

        this.setSlimFilters(false);

        // This will setup the map after the animation has finished
        this.expanded = true;
    }

    displayList(mapObj: MapInterface, $event) {
        if (this.mapOnly) {
            return;
        }

        $event.preventDefault();

        // Shrink the map
        this.mapElm.nativeElement?.classList.remove(EXPAND_MAP);

        // Restore list elems
        this.sortElm.nativeElement?.classList.remove(HIDE_SORT);
        this.listElm.nativeElement?.classList.remove(HIDE_LIST);
        // Update fallback class
        this.hideList = '';
        this.hrElm.nativeElement?.classList.remove(HIDE_HR);

        this.expanded = false;

        if (this.hashEntityId) {
            this.removeMapIdFromURI();
        }

    }

    clearAllFilters() {
        this.filters.clearPills();
    }

    setSlimFilters(state: boolean) {
        let enableSlimFilters = false;
        if (this.listElm) {
            enableSlimFilters = !this.listElm.nativeElement.classList.contains(HIDE_LIST) && state;
        }
        this.slimFilters = enableSlimFilters;
        this.filterHeight = enableSlimFilters ? this.filtersElem.nativeElement.offsetHeight : 0;

        if (this.filters) {
            this.filters.shrinkButtons = enableSlimFilters || this.shrinkFilters;
        }
    }

    setValidatingProcessState(state: boolean) {
        this.validatingProcess = state;
    }

    addCardsToList(cardStatus) {
        if (cardStatus.isIntersecting) {
            this.updatePartialListData();
        }
    }

    startWebSocketConnection(updatePermissions) {
        if (!updatePermissions) {
            return;
        }

        this.webSocketService.listConnection(updatePermissions);
    }

    private checkLocationParam(locations: string[]) {
        if (!this.route.snapshot.params.park) {
            return locations;
        }
        const { park } = this.route.snapshot.params;

        const locationOption = this.locationsOptions.find(l => l.urlFriendlyId === park);

        if (!locationOption) {
            return locations;
        }

        const isLocationApplied = locations.find(l => l === locationOption.urlFriendlyId);

        if (isLocationApplied) {
            return locations;
        }

        locations.push(locationOption.key);

        return locations;
    }

    private updateHash() {
        let entityId;
        const locations = this.getSelectedLocations();
        const validatedLocations = this.validateLocationParam(locations);

        // As long as any filter (by location or top filters) has been selected,
        // the map ID URL is shown. If at least one filter is selected the ID is removed
        if (isEmpty(this.appliedFacets) && isEmpty(locations)) {
            entityId = this.hashEntityId;
        }

        const sortOption = this.sortOptions.find(option => option.key === this.selectedSortOption);
        const sortOptionHash = get(sortOption, 'hash');
        this.filtersHash.updateHash(this.appliedFacets, validatedLocations, sortOptionHash, entityId);

        if (this.mapOnly) {
            this.updateMapListViewUrl();
            this.setMapListViewButtonUrl();
        }
    }

    // For map-only views of the list page (e.g., lodging map pages), ensure the "List View"
    // URL back to non-Nemo list pages respect the selected facets
    private updateMapListViewUrl() {
        // remove the occurrence of "map" from the URL back to the list view
        const pathName = this.winRef.nativeWindow.location.pathname
            .split('/').filter(value => value !== '' && !value.includes('map'));

        // keep the sorting option from the hash
        const hashName = this.winRef.nativeWindow.location.hash;

        // now set the URL of the button
        this.listControl.mapHostPageUrl = `/${pathName.join('/')}/${hashName}`;
    }

    // For map-only views of the list page (e.g., lodging map pages), set the "List View" button's URL
    // calculated on each hash update in updateMapListViewUrl()
    private setMapListViewButtonUrl() {
        this.getElementRef().querySelector('#mapSizeControl').setAttribute('href', this.listControl.mapHostPageUrl);
    }

    private getSelectedLocations() {
        // locationsSelected is an array of strings containing every selected locationsOptions key
        // the next statement builds an array of strings but now containing the urlFriendlyId of every locationsSelected
        const locationNamesSelected = [];
        this.locationsSelected.forEach((location) => {
            const locationObject = this.locationsOptions.find(locationOption => locationOption.key === location);
            if (locationObject && locationObject.urlFriendlyId) {
                return locationNamesSelected.push(locationObject.urlFriendlyId);
            }
        });

        return locationNamesSelected;
    }

    private validateLocationParam(locations) {
        // location param is used only when there is one location selected
        if (locations.length !== 1) {
            // cleanup param if set
            this.updateLocationParam(this.parkParam, '');
            this.parkParam = '';

            return locations;
        }

        const oldParam = this.parkParam || /$/;
        const newParam = `/${locations[0]}/`;
        this.parkParam = locations[0];

        this.updateLocationParam(oldParam, newParam);

        return [];
    }

    private updateLocationParam(oldParam, newParam) {
        if (oldParam === newParam || oldParam === 'map') {
            return;
        }
        const updatedUrl = this.winRef.nativeWindow.location.pathname
            .replace(oldParam, newParam)
            .replace(/\/\//g, '/');

        history.replaceState({}, this.document.title, updatedUrl);
    }

    private updateListData() {
        this.listService.filterSortOptions({
            sort: {
                type: this.selectedSortOption
            },
            filter: {
                appliedFacets: this.appliedFacets,
                locations: this.locationsSelected
            }
        });
    }

    /**
     * Setup the defaults passed into the map component
     */
    private setupMapDefaults() {
        this.expandControl = {
            text: this.listData.map.labels.expandMapText,
            title: this.listData.map.labels.expandMapText,
            icon: 'expand-icon',
            callback: this.expandMap.bind(this)
        };

        this.listControl = {
            text: this.listData.map.labels.listViewText,
            title: this.listData.map.labels.listViewText,
            icon: 'display-list',
            callback: this.displayList.bind(this)
        };

        // when mapOnly is true, add path URL to the listControl config so
        // an HREF will be set on list view button
        if (this.mapOnly) {
            this.updateMapListViewUrl();
        }

        // Clone the map defaults from the result so we dont change the original response
        this.mapDefaults = cloneDeep(this.listData.map.location.defaults);

        // Set the zoom controls in the top right
        this.mapDefaults.zoomOptions = {
            position: 'topright'
        };

        this.mapDefaults.addExpandControl = this.expandControl;
        this.mapDefaults.dragging = true;
    }

    private setupMapMarkersAndCards() {
        this.listData$
            .pipe(
                debounceTime(50),
                /**
                 * The first result comes from the default BehaviorSubject. It's
                 * empty. Ignore it.
                 */
                filter(data => !isEmpty(data)),
                map((data) => {
                    this.groups = data.groups;
                    this.size = data.size;
                    this.markers = data.markers;
                    this.listCards = {};

                    // Reset the partial listData
                    this.listService.resetPartialData();
                    // update url hash based on filters
                    this.updateHash();
                    // Listen for changes from the data stream and update the map
                    if (this.map && data.markers) {
                        if (this.hashEntityId && !this.mapPinFromURI) {
                            this.mapPinFromURI = data.markers.find(marker => marker.card.urlFriendlyId === this.hashEntityId);
                        }
                        // This is the rxjs version of setTimeout
                        asapScheduler.schedule(() => this.map.setMarkers(data.markers, this.markerOptions));
                    }
                }),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.loaded = true;
                this.updatePartialListData();
            });
    }

    private updatePartialListData() {
        this.listService.updatePartialListData(this.listCards);
    }

    private setupMapEventListeners() {
        const transitions = new Map();
        // The height changes when expanding/contracting the map
        transitions.set('height', false);

        // Listen for animations to finish before making changes to the map
        fromEvent(this.mapElm.nativeElement, 'transitionend')
            .pipe(
                takeUntil(this.destroy$),
                filter(($event: any) => {
                    /**
                     * We want to make sure the height and opacity changes have happened before
                     * allowing the map to update. There's a nasty race condition where the map
                     * object doesn't exist if the animations are still occuring.
                     */
                    transitions.set($event.propertyName, true);

                    return Array.from(transitions).every(([key, value]) => value === true);
                }),
            )
            .subscribe(() => {
                if (this.expanded) {
                    // Remove expand button
                    this.map.removeExpandControl(this.expandControl);

                    // Add the list view button
                    this.map.addListControl(this.listControl);

                    // Call zoom position to ensure proper placement below "List View" button
                    this.map.setZoomControls({
                        position: 'topright'
                    });

                } else {
                    // Remove list button
                    this.map.removeListControl(this.listControl);

                    // Add expand control
                    this.map.addExpandControl(this.expandControl);

                    // Call zoom position to ensure proper placement below "Expand Map" button
                    this.map.setZoomControls({
                        position: 'topright'
                    });
                }

                // Only focus on pins if we're not deep linked to one
                if (!this.hashEntityId) {
                    this.map.focusOnPins();
                }

                // Reset the transition we're looking for
                transitions.set('height', false);
            });
    }

    private setupListHeaderSwipe() {
        let touchstart: number;

        fromEvent(this.listHeader.nativeElement, 'touchstart')
            .pipe(
                switchMap((event: TouchEvent) => {
                    touchstart = event.touches[0].clientY;

                    return fromEvent(this.listHeader.nativeElement, 'touchend');
                }),
                takeUntil(this.destroy$)
            )
            .subscribe((event: TouchEvent) => {
                const isExpanded = this.mapElm.nativeElement.classList.contains(EXPAND_MAP);
                const touchend = event.changedTouches[0].clientY;
                if ((touchstart > touchend && isExpanded) || (touchstart < touchend && !isExpanded)) {
                    this.shrinkFiltersAndToggleMap(!isExpanded);
                }
            });
    }

    private shrinkFiltersAndToggleMap(shrink: boolean) {
        this.shrinkFilters = shrink;
        this.filters.shrinkButtons = shrink || this.slimFilters;

        if (!this.mapElm) {
            return;
        }

        /**
         * set the view to the map position
         * Safari doesn't support 'scrollIntoViewOptions'
         * see https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoView
         */
        const isSmoothScrollSupported = 'scrollBehavior' in this.mapElm.nativeElement.style;
        /* istanbul ignore else */
        if (isSmoothScrollSupported) {
            this.mapElm.nativeElement.scrollIntoView({ behavior: 'smooth' });
        } else {
            this.mapElm.nativeElement.scrollIntoView();
        }

        this.toggleMap();
    }

    private toggleMap() {
        // Listen for the animation event and then redraw the map
        this.mapElm.nativeElement?.classList.toggle(EXPAND_MAP);
    }

    private getBannerImage() {
        const image = get(this.listData, 'pageBanner');
        if (image) {
            this.pageBanner = {
                desktop: image.url,
                alt: image.alt
            };
        }
    }

    // When coming from a map tile on a detail page to the list page,
    // the ID is passed so the full-screen map focuses on that entity's pin.
    // We need to clear that out when returning to the split list/map view.
    private removeMapIdFromURI() {
        let hashName = this.winRef.nativeWindow.location.hash;
        const pathName = this.winRef.nativeWindow.location.pathname;
        hashName = hashName.split('/').filter(value => value !== '' && !value.includes('id'));
        hashName = hashName.join('/');
        history.pushState({}, this.document.title, `${pathName.concat(hashName)}/`);
        this.hashEntityId = null;
    }
}
